import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Award, CheckCircle2, RotateCcw, XCircle } from 'lucide-react';

import {
  getCoursePlayer,
  submitQuizAttempt,
  type QuizAttemptResult,
  type TakerQuiz,
} from '@/api/training.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { describeTrainingError } from '@/lib/training/errors';
import { useToast } from '@/hooks/useToast';

/**
 * Quiz taking (Stream C, ADR-025) — one multiple-choice question per
 * card, submit-all grading. The quiz arrives taker-sanitized (no
 * correct answers on the wire); the result screen renders the graded
 * snapshot the server returns, which includes the correct option text.
 */

export function QuizTakePage() {
  const { courseId, quizId } = useParams<{ courseId: string; quizId: string }>();

  const playerQuery = useQuery({
    queryKey: ['training', 'course', courseId],
    queryFn: () => getCoursePlayer(courseId ?? ''),
    enabled: Boolean(courseId),
  });

  if (playerQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  const quiz = playerQuery.data?.quizzes.find((q) => q.id === quizId) ?? null;

  if (playerQuery.error || !playerQuery.data || !quiz) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div className="flex-1">
          <p className="text-[14px] font-medium text-kw-danger-text">Quiz not available</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {describeTrainingError(
              playerQuery.error,
              'This quiz does not exist for a course you are enrolled in.',
            )}
          </p>
          <Link to="/training" className="mt-3 inline-block text-[13px] text-kw-cerulean hover:underline">
            ← Back to My Training
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <QuizForm courseId={playerQuery.data.course.id} courseTitle={playerQuery.data.course.title} quiz={quiz} />
  );
}

function QuizForm({
  courseId,
  courseTitle,
  quiz,
}: {
  courseId: string;
  courseTitle: string;
  quiz: TakerQuiz;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<QuizAttemptResult | null>(null);

  const allAnswered = quiz.questions.every((q) => answers[q.id] !== undefined);

  const submitMutation = useMutation({
    mutationFn: () =>
      submitQuizAttempt(quiz.id, {
        answers: quiz.questions.map((q) => ({
          questionId: q.id,
          selectedOptionId: answers[q.id] ?? '',
        })),
      }),
    onSuccess: (attempt) => {
      setResult(attempt);
      void queryClient.invalidateQueries({ queryKey: ['training', 'course', courseId] });
      void queryClient.invalidateQueries({ queryKey: ['training', 'me'] });
    },
    onError: (err) => {
      toast.show(describeTrainingError(err, 'Could not submit the quiz.'), { variant: 'danger' });
    },
  });

  if (result) {
    return (
      <ResultScreen
        courseId={courseId}
        quiz={quiz}
        result={result}
        onRetry={() => {
          setAnswers({});
          setResult(null);
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <Link
          to={`/training/courses/${courseId}`}
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          {courseTitle}
        </Link>
      </div>

      <div>
        <h1 className="typo-h1">{quiz.title}</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          {quiz.questions.length} question{quiz.questions.length === 1 ? '' : 's'} · pass at{' '}
          {quiz.passThresholdPercent}%
          {quiz.maxAttempts !== null
            ? ` · ${String(quiz.maxAttempts)} attempt${quiz.maxAttempts === 1 ? '' : 's'} allowed`
            : ' · unlimited attempts'}
        </p>
      </div>

      {quiz.questions.map((question, index) => (
        <Card key={question.id} className="p-5">
          <fieldset>
            <legend className="text-[14px] font-medium text-kw-shark">
              {index + 1}. {question.prompt}
            </legend>
            <div className="mt-3 space-y-2">
              {question.optionsJson.map((option) => (
                <label
                  key={option.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-kw-md border border-kw-border px-3 py-2.5 text-[14px] text-kw-shark hover:bg-kw-bg has-[:checked]:border-kw-cerulean has-[:checked]:bg-kw-cerulean-light"
                >
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option.id}
                    checked={answers[question.id] === option.id}
                    onChange={() => {
                      setAnswers((prev) => ({ ...prev, [question.id]: option.id }));
                    }}
                    className="size-4 border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
                  />
                  {option.text}
                </label>
              ))}
            </div>
          </fieldset>
        </Card>
      ))}

      <div className="flex items-center justify-end gap-3">
        {!allAnswered ? <p className="text-[12px] text-kw-muted">Answer every question to submit.</p> : null}
        <Button
          disabled={!allAnswered}
          isLoading={submitMutation.isPending}
          onClick={() => {
            submitMutation.mutate();
          }}
        >
          Submit answers
        </Button>
      </div>
    </div>
  );
}

function ResultScreen({
  courseId,
  quiz,
  result,
  onRetry,
}: {
  courseId: string;
  quiz: TakerQuiz;
  result: QuizAttemptResult;
  onRetry: () => void;
}) {
  const navigate = useNavigate();
  const attemptsRemaining =
    quiz.maxAttempts === null ? null : Math.max(0, quiz.maxAttempts - result.attemptNumber);
  const canRetry = !result.passed && (attemptsRemaining === null || attemptsRemaining > 0);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Card
        className={`p-6 text-center ${
          result.passed
            ? 'border-kw-tag-text/30 bg-kw-emerald-light'
            : 'border-kw-danger-text/30 bg-kw-danger-bg'
        }`}
      >
        {result.passed ? (
          <CheckCircle2 className="mx-auto size-10 text-kw-tag-text" />
        ) : (
          <XCircle className="mx-auto size-10 text-kw-danger-text" />
        )}
        <p
          className={`mt-2 text-[22px] font-semibold ${
            result.passed ? 'text-kw-tag-text' : 'text-kw-danger-text'
          }`}
        >
          {result.scorePercent}% — {result.passed ? 'Passed' : 'Not passed'}
        </p>
        <p className={`mt-1 text-[13px] ${result.passed ? 'text-kw-tag-text/80' : 'text-kw-danger-text/80'}`}>
          Attempt {result.attemptNumber}
          {quiz.maxAttempts !== null ? ` of ${String(quiz.maxAttempts)}` : ''} · pass threshold{' '}
          {quiz.passThresholdPercent}%
        </p>
        {attemptsRemaining !== null && !result.passed ? (
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {attemptsRemaining > 0
              ? `${String(attemptsRemaining)} attempt${attemptsRemaining === 1 ? '' : 's'} remaining.`
              : 'No attempts remaining — contact your training admin.'}
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {result.passed && result.certificateCode ? (
            <Button
              onClick={() => {
                void navigate(`/training/certificates/${result.certificateCode ?? ''}`);
              }}
            >
              <Award className="size-4" />
              View certificate
            </Button>
          ) : null}
          {canRetry ? (
            <Button onClick={onRetry}>
              <RotateCcw className="size-4" />
              Retry quiz
            </Button>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => {
              void navigate(`/training/courses/${courseId}`);
            }}
          >
            Back to course
          </Button>
        </div>
      </Card>

      {/* Per-question review from the graded snapshot */}
      <section className="space-y-2">
        <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Answer review</h2>
        {result.answersSnapshot.map((entry, index) => (
          <Card key={entry.questionId} className="p-4">
            <div className="flex items-start gap-2.5">
              {entry.correct ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-kw-tag-text" />
              ) : (
                <XCircle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-medium text-kw-shark">
                  {index + 1}. {entry.prompt}
                </p>
                <p className="mt-1 text-[13px] text-kw-shark">
                  Your answer:{' '}
                  <span className={entry.correct ? 'text-kw-tag-text' : 'text-kw-danger-text'}>
                    {entry.chosenOptionText ?? 'Not answered'}
                  </span>
                </p>
                {!entry.correct && entry.correctOptionText !== null ? (
                  <p className="mt-0.5 text-[13px] text-kw-muted">
                    Correct answer: {entry.correctOptionText}
                  </p>
                ) : null}
              </div>
              <Badge variant={entry.correct ? 'success' : 'danger'}>
                {entry.correct ? 'Correct' : 'Incorrect'}
              </Badge>
            </div>
          </Card>
        ))}
      </section>
    </div>
  );
}
